import React, { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  numInputs?: number;
  primaryColor?: string;
  isDisabled?: boolean;
  autoFocus?: boolean;
}

export const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  onComplete,
  numInputs = 6,
  primaryColor,
  isDisabled = false,
  autoFocus = false,
}) => {
  const [activeInput, setActiveInput] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, numInputs);
  }, [numInputs]);

  // Handle autoFocus
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const val = e.target.value;
    if (!val) return;

    // Only allow numbers
    if (!/^\d+$/.test(val)) return;

    const newValue = value.split('');
    newValue[index] = val.slice(-1); // Take the last character if someone typed multiple
    const updatedValue = newValue.join('');
    onChange(updatedValue);

    // If complete, trigger onComplete
    if (updatedValue.length === numInputs && onComplete) {
      onComplete(updatedValue);
    }

    // Move to next input
    if (index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveInput(index + 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        // Move to previous input and clear it
        const newValue = value.split('');
        newValue[index - 1] = '';
        onChange(newValue.join(''));
        inputRefs.current[index - 1]?.focus();
        setActiveInput(index - 1);
      } else {
        // Just clear current input
        const newValue = value.split('');
        newValue[index] = '';
        onChange(newValue.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setActiveInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveInput(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, numInputs);
    if (!pastedData) return;

    const newValue = pastedData.padEnd(numInputs, '').slice(0, numInputs);
    onChange(newValue);
    
    if (pastedData.length === numInputs && onComplete) {
      onComplete(pastedData);
    }

    // Focus the last filled input or the first empty one
    const nextIndex = Math.min(pastedData.length, numInputs - 1);
    inputRefs.current[nextIndex]?.focus();
    setActiveInput(nextIndex);
  };

  return (
    <div className="flex justify-center gap-1.5 md:gap-2 w-full">
      {Array.from({ length: numInputs }).map((_, index) => (
        <div key={index} className="relative">
          <input
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{1}"
            value={value[index] || ''}
            onChange={(e) => handleInputChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            onFocus={() => setActiveInput(index)}
            disabled={isDisabled}
            className={clsx(
              "w-9 h-11 md:w-11 md:h-13 text-center text-lg md:text-xl font-bold border-2 rounded-lg md:rounded-xl transition-all outline-none text-gray-900",
              activeInput === index ? "border-black" : "border-gray-200",
              isDisabled && "bg-gray-50 cursor-not-allowed opacity-50"
            )}
            style={{
              borderColor: (activeInput === index && primaryColor) ? primaryColor : undefined,
            }}
          />
          {!value[index] && (
            <span className="text-gray-200 pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] md:text-xs">
              ●
            </span>
          )}
        </div>
      ))}
    </div>
  );
};
